import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {TvShopEntertainmentPack} from '../../../model/tv-shop-entertainment-pack';
import {NavigableComponent} from '../../../model/navigable-component';
import {NavigationService} from '../../../services/navigation/navigation.service';
import {takeUntil} from 'rxjs/operators';
import {ResourceService} from '../../../services/resource/resource.service';
import {OverlayMessageTrailerComponent} from './overlay-message-trailer/overlay-message-trailer.component';
import {TvShopStateService} from '../../../services/state/tv-shop-state.service';
import Timer = NodeJS.Timer;

@Component({
  selector: 'app-trailer',
  templateUrl: './trailer.component.html',
  styleUrls: ['./trailer.component.less']
})
export class TrailerComponent extends NavigableComponent implements OnInit, OnDestroy, AfterViewInit {

  FORWARD_REWIND_INTERVAL = 10;

  @Input()
  show = false;
  @Input()
  pack: TvShopEntertainmentPack;
  @Input()
  YTvideoId: string;

  @ViewChild('overlayMessageTrailer')
  overlayMessageTrailer: OverlayMessageTrailerComponent;

  @Output()
  emitExitVideo = new EventEmitter<Object>();
  @Output()
  emitVideoReady = new EventEmitter<Object>();

  closeMessageShowed = false;
  YT: any;
  YTplayer: any;

  timingRefreshInterval: Timer;

  newTime: number;
  newTimeTimeoutTimer: Timer;

  constructor(protected navigationService: NavigationService,
              private resourceService: ResourceService,
              private stateService: TvShopStateService) {
    super(navigationService);
  }

  ngOnInit() {
    this.createScriptTag();

    try {
      this.createPlayer();
    } catch (e) {
      window['onYouTubeIframeAPIReady'] = (e) => {
        this.createPlayer();
      };
    }

    this.subscribeToEvents();
  }

  ngAfterViewInit() {
    this.stateService.setIsTrailerPlaying(true);
    this.overlayMessageTrailer.showVideoReadyToPlay();
  }

  onPlayerStateChange(event) {
    switch (event.data) {
      case this.YT.PlayerState.PLAYING:
        this.startTimerUpdate();
        break;
      case this.YT.PlayerState.ENDED:
        this.exitVideo();
        break;
      default:
        clearInterval(this.timingRefreshInterval);
    }
  }

  onPlayerError(event) {
    console.log('Error while loading YT player : ' + event);
    this.exitVideo();
  }

  onPlayerReady(event) {
    this.YTplayer.playVideo();
    this.videoDurationChanged();
    this.startTimerUpdate();
  }

  ngOnDestroy(): void {
    clearInterval(this.timingRefreshInterval);
    clearTimeout(this.newTimeTimeoutTimer);
    if (this.YTplayer) {
      this.YTplayer.stopVideo();
    }
    super.ngOnDestroy();
  }

  forward() {
    this.overlayMessageTrailer.isForwarding();


    if (!this.searchingThroughVideo()) {
      this.newTime = this.YTplayer.getCurrentTime();
    }

    this.newTime = this.newTime + this.FORWARD_REWIND_INTERVAL;
    if (this.newTime > this.YTplayer.getDuration()) {
      this.newTime = this.YTplayer.getDuration();
    }

    this.seekToNewTime();
  }

  rewind() {
    this.overlayMessageTrailer.isRewinding();

    if (!this.searchingThroughVideo()) {
      this.newTime = this.YTplayer.getCurrentTime();
    }

    this.newTime = this.newTime - this.FORWARD_REWIND_INTERVAL;
    if (this.newTime < 0) {
      this.newTime = 0;
    }

    this.seekToNewTime();
  }

  videoDurationChanged() {
    this.overlayMessageTrailer.updateVideoDurationTime(this.YTplayer.getDuration());
  }

  videoTimeUpdate() {
    if (this.searchingThroughVideo()) {
      // we're still searching through the movie, don't count on current time of the player
      return;
    }
    if (this.YTplayer.getDuration() - this.YTplayer.getCurrentTime() < 2) {
      this.exitVideo();
    } else {
      this.overlayMessageTrailer.updateVideoCurrentTime(this.YTplayer.getCurrentTime());

      if (!this.closeMessageShowed && this.YTplayer.getCurrentTime() > 5) {
        this.overlayMessageTrailer.hideVideoReadyToPlay();
        this.closeMessageShowed = true;
      }
    }
  }

  private searchingThroughVideo(): boolean {
    return this.newTime !== undefined;
  }

  private createScriptTag(): void {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }

  private createPlayer(): void {
    this.YT = window['YT'];
    this.YTplayer = new this.YT.Player('player', {
        videoId: this.YTvideoId,
        playerVars: {
          start: 1,
          autoplay: 1,
          controls: 0,
          disablekb: 1,
          playList: this.YTvideoId,
          modestBranding: 1,
          rel: 0,
          playsinline: 0
        },
        right: 0,
        bottom: 0,
        events: {
          'onStateChange': this.onPlayerStateChange.bind(this),
          'onError': this.onPlayerError.bind(this),
          'onReady': this.onPlayerReady.bind(this)
        }
      }
    );
  }

  private subscribeToEvents() {
    this.remoteControllerEvents();
    this.keyBoardEvents();
  }

  private keyBoardEvents() {
    this.backwardPressedOnKeyBoard.pipe(takeUntil(this.stopSubscription)).subscribe(value => value ? this.rewind() : this.doNothing());
    this.fastForwardPressedOnKeyBoard.pipe(takeUntil(this.stopSubscription)).subscribe(value => value ? this.forward() : this.doNothing());
  }

  private remoteControllerEvents() {
    this.rewindPressed.pipe(takeUntil(this.stopSubscription)).subscribe(value => value ? this.rewind() : this.doNothing());
    this.forwardPressed.pipe(takeUntil(this.stopSubscription)).subscribe(value => value ? this.forward() : this.doNothing());
  }

  private seekToNewTime() {
    clearInterval(this.timingRefreshInterval);

    this.overlayMessageTrailer.updateVideoCurrentTime(this.newTime);
    this.YTplayer.seekTo(this.newTime, false);

    this.waitToActivateNewTime();
  }

  private waitToActivateNewTime() {
    clearTimeout(this.newTimeTimeoutTimer);
    this.newTimeTimeoutTimer = setTimeout(() => {
      this.activateNewTime();
    }, 1500);
  }

  private activateNewTime() {
    this.YTplayer.seekTo(this.newTime, true);
    this.newTime = undefined;
    this.startTimerUpdate();
  }

  private startTimerUpdate() {
    this.timingRefreshInterval = setInterval(() => {
      this.videoTimeUpdate();
    }, 500);
  }

  private exitVideo() {
    clearInterval(this.timingRefreshInterval);
    this.YTplayer.stopVideo();
    this.emitExitVideo.emit();
  }

  private doNothing() {
  }
}
